import { Badge } from '@design/components/badge/Badge';
import { Button } from '@design/components/button/Button';
import { CloseTrigger, Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { NumericalCellValueStyle } from '@helpers/utils/table/table';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useThemePerformanceDisplay } from '@terminal/hooks/use-theme-performance-display/useThemePerformanceDisplay';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { EarningsSummary } from './EarningsSummary';
import styles from './ThemeEarningsDrawer.module.scss';

const numericalStyleToClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: semanticColorStyles.directionPositive,
  [NumericalCellValueStyle.Negative]: semanticColorStyles.directionNegative,
  [NumericalCellValueStyle.Neutral]:
    semanticColorStyles.directionNeutralDefault,
};

export interface ThemeEarningsDrawerProps {
  isOpen: boolean;
  isNestedDrawerOpen?: boolean;
  onClose: () => void;
  themeId: string | undefined;
}

export const ThemeEarningsDrawer = ({
  isOpen,
  isNestedDrawerOpen = false,
  onClose,
  themeId,
}: ThemeEarningsDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const {
    data: theme,
    isError,
    isLoading,
  } = useTheme({
    themeId: isOpen ? themeId : undefined,
  });
  const { displayValue, numericalStyle } = useThemePerformanceDisplay(
    isOpen ? themeId : undefined
  );

  const handleOpenThemeOverview = () => {
    navigateToTheme({
      themeId: theme?.id,
      source: POSTHOG_TRACKING_SOURCES.THEME_EARNINGS_DRAWER,
    });
  };

  const handleViewFullSummary = () => {
    const searchParams = new URLSearchParams();
    searchParams.set(queryKeys.activeSubTab, 'earnings');
    navigateToTheme({
      themeId: theme?.id,
      source: POSTHOG_TRACKING_SOURCES.THEME_EARNINGS_DRAWER,
      searchParams,
    });
  };

  return (
    <Drawer
      data-testid="theme-earnings-drawer"
      isOpen={isOpen}
      handleCloseClick={onClose}
      closeTriggers={
        isNestedDrawerOpen
          ? [CloseTrigger.CloseButtonClick, CloseTrigger.BackgroundClick]
          : undefined
      }
      headerButton={
        theme && (
          <WebOnlyFeat>
            <Button
              variant="empty"
              label={t('widget:assetOverview.openThemeOverview')}
              iconName="Expand"
              size="xsmall"
              onClick={handleOpenThemeOverview}
            />
          </WebOnlyFeat>
        )
      }
    >
      <DrawerContentWrapper className={styles.content}>
        <ErrorBoundaryWrapper resetKeys={[isOpen, theme?.id]}>
          {isError ? (
            <ErrorMessageWrapper displayIcon showBorder={false} />
          ) : isLoading || !theme ? (
            <SkeletonLoader
              data-testid="theme-earnings-drawer-loading"
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '312px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          ) : (
            <>
              <Badge
                className={styles.themeBadge}
                color="neutral"
                leftSection={
                  <ThemeLogoIcon
                    logo_url={theme.logo_url}
                    name={theme.name}
                    type={theme.type}
                    size={16}
                  />
                }
                label={
                  <>
                    {theme.name}
                    {displayValue !== undefined && (
                      <span
                        className={clsx(
                          styles.performance,
                          numericalStyleToClass[numericalStyle]
                        )}
                      >
                        {formatPercentageNumber({
                          value: displayValue,
                          includePercentSign: true,
                          addPlus: true,
                          formatZero: true,
                        })}
                      </span>
                    )}
                  </>
                }
              />
              <EarningsSummary key={theme.id} variant="drawer" theme={theme} />
              <WebOnlyFeat>
                <Button
                  className={styles.fullSummaryButton}
                  label={t(
                    'widget:themeOverview.tabs.earnings.drawer.viewFullSummary'
                  )}
                  size="small"
                  onClick={handleViewFullSummary}
                  data-testid="view-full-theme-earnings-summary"
                />
              </WebOnlyFeat>
            </>
          )}
        </ErrorBoundaryWrapper>
      </DrawerContentWrapper>
    </Drawer>
  );
};
